import { useCallback, useRef, useState } from "react";
import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import moment from "moment";
import { vidoe_librar_detail_api } from "../../DAL/Programmes/Programmes";
import VideoLibraryCard from "./VideoLibraryCard";
import VideoFilter from "./VideoFilter";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrCard from "src/design-system/components/GrCard";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrFilterDrawer from "src/design-system/components/GrFilterDrawer";
import GrFilterButton from "src/design-system/components/GrFilterButton";
import GrActiveFilters from "src/design-system/components/GrActiveFilters";
import { formatDay, htmlText } from "../Programmes/programmeHelpers";
import { isSameRange, lastMonthRange } from "./videoHelpers";

/**
 * One programme's videos (/videos-library/:library_id): date range (last month by default,
 * Filter by date to change it), video cards, load more.
 */

const toolbarSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[3],
  minHeight: gr.control.md,
  pt: gr.space[4],
  mb: gr.space[6],
  borderTop: `1px solid ${gr.color.line}`,
};

export default function VidoeLibraryDetail() {
  const params = useParams();
  const observer = useRef();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [programmesData, setProgrammesData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [loadMorePath, setLoadMorePath] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { state } = useLocation();
  const load_more_path = `api/member/videos_library?page=0&limit=50`;

  const [filterDrawerState, setFilterDrawerState] = useState(false);
  const EMPTY_FILTER = lastMonthRange();

  const handleSelectOther = (name, value) => {
    setFilterData((values) => ({ ...values, [name]: value }));
  };

  const [filterData, setFilterData] = useState(EMPTY_FILTER);
  // The range the list was loaded with (the drawer may hold picks not applied yet).
  const [appliedRange, setAppliedRange] = useState(EMPTY_FILTER);

  const handleOpenFilterDrawer = () => {
    setFilterDrawerState(true);
  };

  const handleCloseFilterDrawer = () => {
    setFilterDrawerState(false);
  };

  const getProgrammesList = async (path, loading, range) => {
    if (!loading) {
      setIsLoading(true);
      path = load_more_path;
    }

    let postData = { ...range };
    if (postData.start) {
      postData.start = moment(postData.start).format("YYYY-MM-DD");
    }
    if (postData.end) {
      postData.end = moment(postData.end).format("YYYY-MM-DD");
    }
    handleCloseFilterDrawer();

    const result = await vidoe_librar_detail_api(
      path,
      params.library_id,
      postData.start,
      postData.end,
    );
    if (result.code === 200) {
      setLoadMorePath(result?.meta?.load_more);
      setTotalPages(result?.meta?.totalPages);
      if (loading) {
        let new_array = programmesData.concat(result.video_library);
        setProgrammesData(new_array);
        setPageNumber((prev) => prev + 1);
      } else {
        setProgrammesData(result.video_library);
        setPageNumber(1);
        setAppliedRange({ start: range.start, end: range.end });
      }
      setIsLoading(false);
      setIsLoadingMore(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const searchFunction = () => {
    if (!filterData.start && !filterData.end) {
      enqueueSnackbar("Please select date", { variant: "error" });
      return;
    }
    if (filterData.start && filterData.end) {
      getProgrammesList(loadMorePath, false, filterData);
    }
  };

  const handleClearFilter = () => {
    getProgrammesList(loadMorePath, false, EMPTY_FILTER);
    setFilterData(EMPTY_FILTER);
  };

  const loadMoreData = () => {
    setIsLoadingMore(true);
    // Next page of the list on screen: the applied dates, not picks still open in the drawer.
    getProgrammesList(loadMorePath, true, appliedRange);
  };

  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && totalPages >= pageNumber) {
          loadMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, totalPages, pageNumber, loadMorePath, observer],
  );

  useEffect(() => {
    getProgrammesList(load_more_path, false, filterData);
  }, []);

  const title = htmlText(state?.title || state?._id?.title) || "Videos";
  const changedRange = !isSameRange(appliedRange, EMPTY_FILTER);
  // The range always shows as a chip (the default month too); × appears once it differs from the default.
  const activeFilters = [
    {
      key: "range",
      icon: icons.calendar,
      label: `${formatDay(appliedRange.start)} – ${formatDay(appliedRange.end)}`,
      onClear: changedRange ? handleClearFilter : undefined,
    },
  ];
  const filterButton = (size) => (
    <GrFilterButton
      size={size}
      icon={icons.calendar}
      count={changedRange ? 1 : 0}
      onClick={handleOpenFilterDrawer}
    >
      Filter by date
    </GrFilterButton>
  );

  return (
    <GrPage>
      <GrPageHeader
        sx={{ mb: gr.space[5] }}
        onBack={() => navigate(-1)}
        title={title}
        subtitle="Videos from this programme."
        actions={filterButton()}
      />

      <Box sx={toolbarSx}>
        <GrActiveFilters filters={activeFilters} onOpen={handleOpenFilterDrawer} />
      </Box>

      {isLoading ? (
        <GrLoader label="Loading videos" />
      ) : programmesData && programmesData.length > 0 ? (
        <VideoLibraryCard videos={programmesData} />
      ) : (
        <GrCard>
          <GrEmptyState
            icon={<GrIcon icon={icons.video} />}
            title={changedRange ? "No videos between these dates" : "No videos in the last month"}
            text="Pick other dates to see more videos."
            action={filterButton("sm")}
          />
        </GrCard>
      )}

      {!isLoading && totalPages >= pageNumber ? (
        <Box
          id="load-more-feed"
          ref={lastBookElementRef}
          sx={{ display: "flex", justifyContent: "center", mt: gr.space[6] }}
        >
          {isLoadingMore ? (
            <GrLoader inline size="sm" label="Loading more videos" />
          ) : (
            <GrButton variant="text" onClick={loadMoreData}>
              Load more
            </GrButton>
          )}
        </Box>
      ) : null}

      <GrFilterDrawer
        open={filterDrawerState}
        onClose={handleCloseFilterDrawer}
        title="Filter by date"
        onClear={handleClearFilter}
        clearLabel="Reset"
        onApply={searchFunction}
        applyLabel="Apply"
      >
        <VideoFilter filterData={filterData} handleSelectOther={handleSelectOther} />
      </GrFilterDrawer>
    </GrPage>
  );
}
